import React, { useMemo } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getColorForDate } from "../colors";
import { processBinDataWithRange } from "../processBinData";

function pctFieldKey(dateKey) {
  return `pct_${dateKey}`;
}

export default function NumberFrequencyVsBinByDateChart({
  graphData,
  selectedUnit,
  isMobile,
  selectedBinSize,
}) {
  const {
    chartRows,
    sortedDateKeys,
    dateKeyToLabel,
    yDomain,
    yTicks,
    yTickFormatter,
  } = useMemo(() => {
    const binOpts = {
      unitValue: selectedUnit.value,
      binSizeValue: selectedBinSize?.value,
    };
    const lengths = graphData.map((d) => d.length);
    const minL = Math.min(...lengths);
    const maxL = Math.max(...lengths);

    const keys = [
      ...new Set(graphData.map((d) => d.sampleDateKey || "unknown")),
    ];
    keys.sort((a, b) => a.localeCompare(b));

    const labelByKey = {};
    graphData.forEach((d) => {
      const k = d.sampleDateKey || "unknown";
      if (labelByKey[k] == null && d.sampleDateLabel != null) {
        labelByKey[k] = d.sampleDateLabel;
      }
    });
    keys.forEach((k) => {
      if (labelByKey[k] == null) labelByKey[k] = k;
    });

    const masterBins = processBinDataWithRange(
      graphData,
      binOpts,
      minL,
      maxL
    );

    const totalsByDate = {};
    const binsByDate = {};
    keys.forEach((dk) => {
      const subset = graphData.filter(
        (d) => (d.sampleDateKey || "unknown") === dk
      );
      totalsByDate[dk] = subset.length;
      binsByDate[dk] = processBinDataWithRange(subset, binOpts, minL, maxL);
    });

    const rows = masterBins.map((binRow, i) => {
      const row = { binLabel: binRow.binLabel };
      keys.forEach((dk) => {
        const total = totalsByDate[dk] || 0;
        const b = binsByDate[dk][i];
        const n = b ? Math.floor(b.number) : 0;
        row[pctFieldKey(dk)] = total > 0 ? (n / total) * 100 : 0;
        row[`n_${dk}`] = n;
      });
      return row;
    });

    let maxPct = 0;
    rows.forEach((row) => {
      keys.forEach((dk) => {
        const v = row[pctFieldKey(dk)] || 0;
        if (v > maxPct) maxPct = v;
      });
    });

    const yUpper = Math.max(10, Math.ceil(maxPct * 2) / 2);
    const yTickList = [];
    for (let v = 0; v <= yUpper + 1e-9; v += 1) {
      yTickList.push(Number(v.toFixed(1)));
    }
    const yDom = [0, yTickList[yTickList.length - 1] || 10];
    const yFmt = (v) => (Number.isInteger(v) ? `${v}.0` : v.toFixed(1));

    return {
      chartRows: rows,
      sortedDateKeys: keys,
      dateKeyToLabel: labelByKey,
      yDomain: yDom,
      yTicks: yTickList,
      yTickFormatter: yFmt,
    };
  }, [graphData, selectedUnit.value, selectedBinSize?.value]);

  const commonProps = {
    width: "100%",
    height: isMobile ? 500 : 700,
    data: graphData,
    margin: {
      top: isMobile ? 20 : 40,
      right: isMobile ? 20 : 60,
      left: isMobile ? 20 : 80,
      bottom: isMobile ? 80 : 120,
    },
  };

  const barSize = Math.max(6, Math.min(24, Math.floor(28 / sortedDateKeys.length)));

  return (
    <ResponsiveContainer {...{ ...commonProps, height: isMobile ? 600 : 750 }}>
      <ComposedChart data={chartRows}>
        <CartesianGrid strokeDasharray="3 3" stroke="#000000" strokeWidth={1} />
        <XAxis
          dataKey="binLabel"
          label={{
            value: `Length Bin (${selectedUnit.value === "English" ? "Inches" : "mm"})`,
            position: "outsideBottom",
            offset: isMobile ? 20 : 25,
            fontSize: isMobile ? 14 : 18,
            fontWeight: "bold",
            fill: "#000000",
          }}
          angle={-90}
          textAnchor="end"
          height={isMobile ? 160 : 200}
          interval={0}
          tickLine={false}
          allowDuplicatedCategory={false}
          stroke="#000000"
          strokeWidth={2}
          tick={({ x, y, payload }) => {
            const binItem = chartRows.find((b) => b.binLabel === payload.value);
            const isEmpty =
              binItem &&
              sortedDateKeys.every((dk) => (binItem[`n_${dk}`] || 0) === 0);
            return (
              <text
                x={x}
                y={y + 12}
                textAnchor="end"
                fontSize={isMobile ? 10 : 12}
                fontWeight="bold"
                fill={isEmpty ? "#ff6b6b" : "#000000"}
                transform={`rotate(-90, ${x}, ${y + 10})`}
              >
                {payload.value}
              </text>
            );
          }}
        />
        <YAxis
          label={{
            value: "Percentage of Population",
            angle: -90,
            position: "insideLeft",
            style: { textAnchor: "middle" },
            fontSize: isMobile ? 14 : 18,
            fontWeight: "bold",
            fill: "#000000",
          }}
          domain={yDomain}
          tick={{
            fontSize: isMobile ? 10 : 12,
            fontWeight: "bold",
            fill: "#000000",
          }}
          ticks={yTicks}
          tickFormatter={yTickFormatter}
          stroke="#000000"
          strokeWidth={2}
        />
        <Tooltip
          content={({ active, payload, label }) => {
            if (!active || !payload || !payload.length) return null;
            const bin = payload[0].payload;
            return (
              <div
                className="custom-tooltip"
                style={{
                  background: "#fff",
                  border: "1px solid #ddd",
                  borderRadius: "2px",
                  padding: "8px 10px",
                  fontSize: "13px",
                  lineHeight: "1.5",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  minWidth: "200px",
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "6px",
                    borderBottom: "1px solid #eee",
                    paddingBottom: "4px",
                  }}
                >
                  Length Bin: {label}{" "}
                  {selectedUnit.value === "English" ? "Inches" : "mm"}
                </div>
                <div
                  style={{
                    display: "grid",
                    gap: "6px",
                    fontSize: "12px",
                    color: "#666",
                  }}
                >
                  {sortedDateKeys.map((dk) => {
                    const n = bin[`n_${dk}`] ?? 0;
                    const pct = bin[pctFieldKey(dk)] ?? 0;
                    return (
                      <div key={dk}>
                        <div
                          style={{
                            fontWeight: "600",
                            color: getColorForDate(dk, sortedDateKeys),
                            marginBottom: "2px",
                          }}
                        >
                          {dateKeyToLabel[dk]}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: "12px",
                          }}
                        >
                          <span>Number:</span>
                          <span style={{ fontWeight: "500" }}>{n}</span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: "12px",
                          }}
                        >
                          <span>% within session:</span>
                          <span style={{ fontWeight: "500" }}>
                            {pct.toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          }}
        />
        <Legend
          layout="horizontal"
          verticalAlign="top"
          align="center"
          wrapperStyle={{
            paddingBottom: isMobile ? 12 : 20,
            fontSize: isMobile ? 11 : 13,
          }}
        />
        {sortedDateKeys.map((dk) => (
          <Bar
            key={dk}
            dataKey={pctFieldKey(dk)}
            name={dateKeyToLabel[dk]}
            fill={getColorForDate(dk, sortedDateKeys)}
            barSize={barSize}
          />
        ))}
      </ComposedChart>
    </ResponsiveContainer>
  );
}
